import React, { useEffect, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { ArrowUp, ArrowDown, X, Plus, ChevronDown } from 'lucide-react';
import { getTagClassOrder, setTagClassOrder } from '@/lib/tagRules';

const INPUT = 'h-8 rounded-lg border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]';
const BTN = 'grid h-6 w-6 place-items-center rounded text-muted-foreground hover:text-foreground disabled:opacity-30';

// Super admin: order of classification groups on tags (unlisted ones follow in their usual order).
export function TagClassOrder({ classes = [] }) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(getTagClassOrder());
  const [add, setAdd] = useState('');
  useEffect(() => { if (open) notesApi.get('/notes/tag-workspace/tag-class-order').then((r) => setList(r.data || [])).catch(() => {}); }, [open]);
  const save = async (next) => {
    setList(next);
    try { const { data } = await notesApi.put('/notes/tag-workspace/admin/tag-class-order', { order: next }); setList(data); setTagClassOrder(data); }
    catch (e) { toast.error(extractError(e, 'Could not save the classification order.')); }
  };
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; save(n); };
  const onAdd = (e) => {
    e.preventDefault();
    const v = add.trim().toUpperCase();
    if (!v || list.includes(v)) { setAdd(''); return; }
    save([...list, v]); setAdd('');
  };
  const names = [...new Set(classes.flatMap((c) => [c.tag_display_name, c.code]).filter(Boolean).map((x) => x.toUpperCase()))];
  return (
    <div className="mt-3 rounded-xl border border-border bg-background" data-testid="tagws-class-order">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-3 py-2 text-left text-xs" data-testid="tagws-class-order-toggle">
        <span><span className="font-semibold text-foreground">Classification order on tags</span> <span className="text-muted-foreground">{list.length ? list.join(' → ') : 'not set: groups show in their usual order'}</span></span>
        <ChevronDown size={14} className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <div className="space-y-2 border-t border-border px-3 py-3">
          <p className="text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground">Listed classifications (tag display name or code) show first, in this order; any others follow as usual. Inside each classification, lines follow the order of the rules that labelled them.</p>
          {list.map((c, i) => (
            <div key={c} className="flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-sm" data-testid="tagws-class-order-item">
              <span className="w-6 text-xs text-muted-foreground">{i + 1}</span>
              <span className="flex-1 font-mono">{c}</span>
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" className={BTN} data-testid="tagws-class-order-up"><ArrowUp size={13} /></button>
              <button type="button" disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="Move down" className={BTN} data-testid="tagws-class-order-down"><ArrowDown size={13} /></button>
              <button type="button" onClick={() => save(list.filter((x) => x !== c))} aria-label="Remove" className={`${BTN} hover:text-destructive`} data-testid="tagws-class-order-remove"><X size={13} /></button>
            </div>
          ))}
          <form onSubmit={onAdd} className="flex gap-2">
            <input list="tagws-class-order-names" value={add} onChange={(e) => setAdd(e.target.value)} placeholder="Add classification (e.g. POLKI)" className={`${INPUT} flex-1`} data-testid="tagws-class-order-input" />
            <datalist id="tagws-class-order-names">{names.filter((n) => !list.includes(n)).map((n) => <option key={n} value={n} />)}</datalist>
            <button type="submit" className="inline-flex h-8 items-center gap-1 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground" data-testid="tagws-class-order-add"><Plus size={13} /> Add</button>
          </form>
        </div>
      ) : null}
    </div>
  );
}
